import { util as fabricUtil } from 'fabric';
import { extractControlPointsFromPath, pointsToBezierPath } from './BezierTool';
import { centerlineToPathData } from './wegCenterline';

/**
 * Extract anchor points (the points the Catmull-Rom curve passes *through*)
 * from a Fabric.js Path's parsed path data array.
 *
 * Only M and C endpoints + L endpoints are anchor points.
 * The intermediate bezier control points (cp1, cp2 in a C command) are NOT included.
 * Returns coordinates in path LOCAL space.
 *
 * @param {Array} pathData - Fabric.js path data array (e.g. [['M',x,y],['C',...],['Z']])
 * @returns {Array<{x: number, y: number}>}
 */
export function extractAnchorPoints(pathData) {
  if (!pathData || !Array.isArray(pathData)) return [];
  const anchors = [];
  for (let i = 0; i < pathData.length; i++) {
    const cmd = pathData[i];
    if (cmd[0] === 'M') {
      anchors.push({ x: cmd[1], y: cmd[2] });
    } else if (cmd[0] === 'C') {
      // For closed paths, the last C before Z ends at the M start point — skip it
      // to avoid a phantom duplicate handle stacked on top of handle 0.
      if (pathData[i + 1]?.[0] === 'Z') continue;
      anchors.push({ x: cmd[5], y: cmd[6] });
    } else if (cmd[0] === 'L') {
      if (pathData[i + 1]?.[0] === 'Z') continue; // same logic for L commands
      anchors.push({ x: cmd[1], y: cmd[2] });
    }
    // 'Z' has no coordinates — skip
  }
  return anchors;
}

/**
 * Convert a path-local point to world (canvas) coordinates.
 * Uses the same matrix transform as vertexEditUtils.localToWorld but operates
 * on a fabric.Path instead of a fabric.Polygon.
 *
 * Path local space: coordinates relative to pathOffset (bounding-box center).
 * calcTransformMatrix() translates local (0,0) to the object center in world space.
 *
 * @param {fabric.Path} path
 * @param {{x: number, y: number}} localPoint - Point in path local coordinates
 * @returns {{x: number, y: number}} World (canvas) coordinates
 */
export function pathLocalToWorld(path, localPoint) {
  const matrix = path.calcTransformMatrix();
  const centered = {
    x: localPoint.x - (path.pathOffset?.x ?? 0),
    y: localPoint.y - (path.pathOffset?.y ?? 0),
  };
  return fabricUtil.transformPoint(centered, matrix);
}

/**
 * Convert world (canvas) coordinates to path-local coordinates.
 * Inverse of pathLocalToWorld.
 *
 * @param {fabric.Path} path
 * @param {{x: number, y: number}} worldPoint - Point in canvas coordinates
 * @returns {{x: number, y: number}} Path local coordinates
 */
export function pathWorldToLocal(path, worldPoint) {
  const matrix = path.calcTransformMatrix();
  const invMatrix = fabricUtil.invertTransform(matrix);
  const centered = fabricUtil.transformPoint(worldPoint, invMatrix);
  return {
    x: centered.x + (path.pathOffset?.x ?? 0),
    y: centered.y + (path.pathOffset?.y ?? 0),
  };
}

/**
 * Parse an SVG path string into Fabric.js-style path array.
 * Fabric.js v6 `path.set({ path: string })` stores the string as-is without re-parsing,
 * so we must parse manually to keep path.path as an array after every modification.
 *
 * Supports M, C, L, Z commands (the only commands used by pointsToBezierPath).
 *
 * @param {string} pathString
 * @returns {Array} Fabric.js path array, e.g. [['M',x,y],['C',...],['Z']]
 */
function parsePathStringToArray(pathString) {
  const result = [];
  const parts = pathString.trim().match(/[MCLZ][^MCLZ]*/gi) || [];
  for (const part of parts) {
    const type = part[0].toUpperCase();
    const nums = part.slice(1).replace(/,/g, ' ').trim()
      .split(/\s+/).filter(Boolean).map(Number);
    if (type === 'M') result.push(['M', nums[0], nums[1]]);
    else if (type === 'C') result.push(['C', nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]]);
    else if (type === 'L') result.push(['L', nums[0], nums[1]]);
    else if (type === 'Z') result.push(['Z']);
  }
  return result;
}

/**
 * Recompute a Path's bbox (width/height/pathOffset) from its current `path.path`
 * and reposition the object so that every point keeps its world position — correct
 * for ANY rotation, scale, or flip. Call after `path.path` has been replaced.
 *
 * The bbox is computed from ALL control points (including cp1/cp2 of each C segment)
 * to prevent a curve bowing outside the anchor hull from being clipped.
 *
 * Why not the old `left += (newMin - oldMin)` shortcut (from polygon editing): that
 * shifts the object along WORLD axes, which only equals "keep every point fixed"
 * when angle = 0 and scale = 1. On a rotated or scaled path it drifts a little every
 * call, and across a drag (dozens of `moving` frames) it compounds into a visible
 * jump plus an apparent rotation as the band walks along its rotated axis (#720).
 * The invariant is instead: the object's centre must move by the pathOffset change
 * rotated+scaled into world space — R·S·Δoffset.
 *
 * @param {fabric.Path} path
 */
export function recomputePathBBoxKeepingPosition(path) {
  const centerBefore = path.getCenterPoint();
  const offsetBefore = { x: path.pathOffset.x, y: path.pathOffset.y };

  const allPoints = extractControlPointsFromPath(path.path);
  if (!allPoints || allPoints.length === 0) return;

  const newMinX = Math.min(...allPoints.map(p => p.x));
  const newMinY = Math.min(...allPoints.map(p => p.y));
  const newMaxX = Math.max(...allPoints.map(p => p.x));
  const newMaxY = Math.max(...allPoints.map(p => p.y));

  path.set({
    width: newMaxX - newMinX,
    height: newMaxY - newMinY,
    pathOffset: { x: (newMinX + newMaxX) / 2, y: (newMinY + newMaxY) / 2 },
  });

  // Move the centre by Δpathoffset rotated+scaled into world space (strip the
  // translation column from the transform matrix → rotation+scale only).
  const m = path.calcTransformMatrix();
  const rotateScale = [m[0], m[1], m[2], m[3], 0, 0];
  const offsetDeltaWorld = fabricUtil.transformPoint(
    { x: path.pathOffset.x - offsetBefore.x, y: path.pathOffset.y - offsetBefore.y },
    rotateScale,
  );
  path.setPositionByOrigin(
    { x: centerBefore.x + offsetDeltaWorld.x, y: centerBefore.y + offsetDeltaWorld.y },
    'center',
    'center',
  );
  path.setCoords();
}

/**
 * Apply a new set of anchor points to the path:
 * - Regenerates path data via Catmull-Rom → Cubic Bezier
 * - Updates path.path and recomputes bbox + position (rotation/scale-safe)
 *
 * For a centerline weg (#720, `data.shapeType === 'centerline'`) the path is
 * regenerated via `centerlineToPathData` instead — a polyline for `verlauf:'eckig'`,
 * an open Catmull-Rom bezier for `'kurvig'` — and `data.centerline` is written back
 * from the new anchors so a later width/verlauf panel change (which rebuilds the
 * path FROM `data.centerline` via `applyWegGeometry`) doesn't revert the edit.
 *
 * @param {fabric.Path} path
 * @param {Array<{x: number, y: number}>} anchors - Anchor points in LOCAL coordinates
 * @param {boolean} closePath - Whether to append Z (ignored for centerline wegs)
 */
function applyNewAnchors(path, anchors, closePath) {
  const isCenterline = path.data?.shapeType === 'centerline';
  const newPathString = isCenterline
    ? centerlineToPathData(anchors, path.data.verlauf)
    : pointsToBezierPath(anchors, closePath);
  if (!newPathString) return;

  // Parse string → array before calling set(), because Fabric.js path.set({ path: string })
  // stores the string as-is without re-parsing. path.path must always be an array so that
  // subsequent .map() calls and extractControlPointsFromPath() work correctly.
  const parsedPath = parsePathStringToArray(newPathString);
  path.set({ path: parsedPath });

  recomputePathBBoxKeepingPosition(path);

  // #720: keep data.centerline in lockstep with the edited anchors (same LOCAL/raw
  // path coordinate space they were stored in at creation). Only applyWegGeometry
  // reads it, but a stale value there would silently undo vertex edits on the next
  // width/verlauf change — and it must survive save/load, which serializes `data`.
  if (isCenterline) {
    path.data.centerline = anchors.map(p => ({ x: p.x, y: p.y }));
  }
}

/**
 * Move one anchor point (specified in world/canvas coordinates),
 * re-derive all Catmull-Rom control points, and update the path + bbox.
 *
 * @param {fabric.Path} path
 * @param {number} index - Anchor index to update
 * @param {{x: number, y: number}} worldPoint - New position in canvas coordinates
 */
export function updateAnchorPoint(path, index, worldPoint) {
  const anchors = extractAnchorPoints(path.path);
  if (!anchors || anchors.length === 0) return;

  const localPoint = pathWorldToLocal(path, worldPoint);
  anchors[index] = { x: localPoint.x, y: localPoint.y };

  const closePath = path.path.some(cmd => cmd[0] === 'Z');
  applyNewAnchors(path, anchors, closePath);
}

/**
 * Reposition all canvas handles to match the current anchor point world positions.
 * Call after path data changes externally (undo/redo, properties panel).
 *
 * @param {fabric.Path} path
 * @param {fabric.Circle[]} handles - Handle objects (same order as extractAnchorPoints)
 */
export function positionHandlesFromPath(path, handles) {
  const anchors = extractAnchorPoints(path.path);
  anchors.forEach((localPoint, i) => {
    if (handles[i]) {
      const worldPoint = pathLocalToWorld(path, localPoint);
      handles[i].set({ left: worldPoint.x, top: worldPoint.y });
      handles[i].setCoords();
    }
  });
}

/**
 * Insert a new anchor point after `afterIndex` at the midpoint of the segment
 * between anchor[afterIndex] and anchor[afterIndex + 1].
 * Re-derives Catmull-Rom control points and updates bbox.
 *
 * @param {fabric.Path} path
 * @param {number} afterIndex - Index of the anchor after which to insert
 */
export function addAnchorPoint(path, afterIndex) {
  const anchors = extractAnchorPoints(path.path);
  const closePath = path.path.some(cmd => cmd[0] === 'Z');

  const a = anchors[afterIndex];
  const nextIndex = (afterIndex + 1) % anchors.length;
  const b = anchors[nextIndex];
  const midPoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

  const newAnchors = [
    ...anchors.slice(0, afterIndex + 1),
    midPoint,
    ...anchors.slice(afterIndex + 1),
  ];

  applyNewAnchors(path, newAnchors, closePath);
}

/**
 * Remove the anchor point at `index`.
 * Enforces a minimum anchor count (no-op if already at minimum): 3 for a closed
 * bezier shape, 2 for an open centerline weg (#720 — a straight two-point weg is
 * still a valid path).
 * Re-derives Catmull-Rom control points and updates bbox.
 *
 * @param {fabric.Path} path
 * @param {number} index - Index of the anchor to remove
 */
export function removeAnchorPoint(path, index) {
  const anchors = extractAnchorPoints(path.path);
  const minAnchors = path.data?.shapeType === 'centerline' ? 2 : 3;
  if (anchors.length <= minAnchors) return;
  const closePath = path.path.some(cmd => cmd[0] === 'Z');
  const newAnchors = anchors.filter((_, i) => i !== index);
  applyNewAnchors(path, newAnchors, closePath);
}
